import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { CaretLeft, CaretRight, Lock, LockOpen, CheckCircle, X, PencilSimple } from '@phosphor-icons/react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useLocationContext } from '../contexts/LocationContext';
import { useRealtime } from '../contexts/RealtimeContext';

const DISPATCH_ROLES = new Set(['admin', 'dispatch_user']);
const PLANNING_ROLES = new Set(['admin', 'executive', 'sales_manager']);

function toDateStr(d) {
  return d.toISOString().slice(0, 10);
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return toDateStr(d);
}

function fmtDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    planned:   'chip-neutral',
    locked:    'chip-warning',
    done:      'chip-active',
    cancelled: 'chip-danger',
  };
  const labels = { planned: 'PLANNED', locked: 'LOCKED', done: 'DONE', cancelled: 'CANCELLED' };
  return <span data-testid={`dispatch-status-${status}`} className={map[status] || 'chip-neutral'}>{labels[status] || status.toUpperCase()}</span>;
}

// ── DispatchRow ───────────────────────────────────────────────────────────────

function DispatchRow({ dispatch, userRole, onStatusChange, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const totalKgs = dispatch.line_items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0;
  const isDispatch = DISPATCH_ROLES.has(userRole);
  const isPlanning = PLANNING_ROLES.has(userRole);
  const status = dispatch.status;

  async function changeStatus(newStatus) {
    setLoading(true);
    try {
      await api.patch(`/dispatches/${dispatch.id}/status`, { status: newStatus });
      onStatusChange();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error updating status');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="dispatch-row" style={{ borderBottom: '1px solid hsl(0,0%,10%)', padding: '10px 0' }}>
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', flexWrap: 'wrap', rowGap: 6 }}
           onClick={() => setExpanded(e => !e)}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
            color: 'hsl(0,0%,90%)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {dispatch.customer_name}
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
            color: 'hsl(0,0%,45%)', marginTop: 2 }}>
            {dispatch.dispatch_no} · {dispatch.line_items?.length || 0} items · {totalKgs.toLocaleString()} Kgs
            {dispatch.vehicle_no ? ` · ${dispatch.vehicle_no}` : ''}
          </div>
        </div>
        <StatusBadge status={status} />
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
          {/* planned + any team: Edit */}
          {status === 'planned' && (isPlanning || isDispatch) && (
            <button data-testid="btn-edit" onClick={() => onEdit(dispatch)}
              style={{ ...btnStyle, background: 'hsl(0,0%,10%)', color: 'hsl(0,0%,65%)',
                border: '1px solid hsl(0,0%,20%)' }}>
              <PencilSimple size={12} /> Edit
            </button>
          )}
          {/* locked + dispatch team: Edit */}
          {status === 'locked' && isDispatch && (
            <button data-testid="btn-edit" onClick={() => onEdit(dispatch)}
              style={{ ...btnStyle, background: 'hsl(0,0%,10%)', color: 'hsl(0,0%,65%)',
                border: '1px solid hsl(0,0%,20%)' }}>
              <PencilSimple size={12} /> Edit
            </button>
          )}
          {/* planned + dispatch team: Lock */}
          {status === 'planned' && isDispatch && (
            <button data-testid="btn-lock" disabled={loading} onClick={() => changeStatus('locked')}
              style={{ ...btnStyle, background: 'hsl(43,96%,20%)', color: 'hsl(43,96%,56%)',
                border: '1px solid hsl(43,96%,35%)' }}>
              <Lock size={12} /> Lock
            </button>
          )}
          {/* locked + dispatch team: Unlock + Mark Done */}
          {status === 'locked' && isDispatch && (
            <>
              <button data-testid="btn-unlock" disabled={loading} onClick={() => changeStatus('planned')}
                style={{ ...btnStyle, background: 'hsl(0,0%,10%)', color: 'hsl(0,0%,60%)',
                  border: '1px solid hsl(0,0%,20%)' }}>
                <LockOpen size={12} /> Unlock
              </button>
              <button data-testid="btn-done" disabled={loading} onClick={() => changeStatus('done')}
                style={{ ...btnStyle, background: 'hsl(142,60%,10%)', color: 'hsl(142,60%,50%)',
                  border: '1px solid hsl(142,60%,25%)' }}>
                <CheckCircle size={12} /> Done
              </button>
            </>
          )}
          {/* planned + planning team: Cancel */}
          {status === 'planned' && isPlanning && (
            <button data-testid="btn-cancel" disabled={loading} onClick={() => changeStatus('cancelled')}
              style={{ ...btnStyle, background: 'hsl(0,84%,8%)', color: 'hsl(0,84%,50%)',
                border: '1px solid hsl(0,84%,20%)' }}>
              <X size={12} /> Cancel
            </button>
          )}
        </div>
      </div>
      {/* Expanded line items */}
      {expanded && (
        <div data-testid="dispatch-line-items" style={{ marginTop: 8, paddingLeft: 16,
          borderLeft: '2px solid hsl(0,0%,12%)' }}>
          {(dispatch.line_items || []).map((item, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 16, padding: '4px 0',
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,55%)' }}>
              <span style={{ color: 'hsl(0,0%,70%)', minWidth: 80 }}>{item.order_no}</span>
              <span style={{ flex: 1 }}>{item.product_name}</span>
              {item.sku && <span style={{ color: 'hsl(0,0%,40%)' }}>{item.sku}</span>}
              <span style={{ minWidth: 60, textAlign: 'right' }}>{item.quantity} {item.unit || 'Kgs'}</span>
              {item.batch_no && <span style={{ color: 'hsl(43,96%,45%)' }}>{item.batch_no}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const btnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 8px', fontSize: 10, fontFamily: "'JetBrains Mono', monospace",
  fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
  border: 'none', cursor: 'pointer', borderRadius: 0,
};

// ── TransporterGroup ──────────────────────────────────────────────────────────

function TransporterGroup({ transporter, dispatches, userRole, onStatusChange, onLockAll, onEdit }) {
  const [lockingAll, setLockingAll] = useState(false);
  const totalKgs = dispatches.reduce((s, d) =>
    s + (d.line_items?.reduce((ss, i) => ss + (i.quantity || 0), 0) || 0), 0);
  const isDispatch = DISPATCH_ROLES.has(userRole);
  const hasPlanned = dispatches.some(d => d.status === 'planned');

  // Group by d.group within this transporter
  const byGroup = useMemo(() => {
    const map = new Map();
    for (const d of dispatches) {
      const key = d.group || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    }
    return map;
  }, [dispatches]);

  return (
    <div data-testid="transporter-group" style={{ marginBottom: 20, border: '1px solid hsl(0,0%,12%)',
      background: 'hsl(0,0%,4%)' }}>
      {/* Transporter header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', borderBottom: '1px solid hsl(0,0%,10%)',
        background: 'hsl(0,0%,6%)' }}>
        <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 13, fontWeight: 700,
          color: 'hsl(0,0%,90%)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {transporter}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
            color: 'hsl(0,0%,45%)' }}>
            {totalKgs.toLocaleString()} Kgs
          </span>
          {isDispatch && hasPlanned && (
            <button data-testid="btn-lock-all" onClick={async () => {
                setLockingAll(true);
                await onLockAll(transporter);
                setLockingAll(false);
              }} disabled={lockingAll}
              style={{ ...btnStyle, background: 'hsl(43,96%,15%)', color: 'hsl(43,96%,56%)',
                border: '1px solid hsl(43,96%,30%)' }}>
              <Lock size={10} /> Lock All
            </button>
          )}
        </div>
      </div>
      {/* Groups within transporter */}
      <div style={{ padding: '0 12px' }}>
        {Array.from(byGroup.entries()).map(([group, groupDispatches]) => (
          <div key={group}>
            {group && (
              <div data-testid="dispatch-group-header" style={{ marginTop: 8, marginBottom: 4,
                fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.1em',
                borderBottom: '1px solid hsl(0,0%,8%)', paddingBottom: 2 }}>
                {group} · {groupDispatches.reduce((s, d) =>
                  s + (d.line_items?.reduce((ss, i) => ss + (i.quantity || 0), 0) || 0), 0).toLocaleString()} Kgs
              </div>
            )}
            {groupDispatches.map(d => (
              <DispatchRow key={d.id} dispatch={d} userRole={userRole} onStatusChange={onStatusChange} onEdit={onEdit} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── CreateDispatchDrawer ──────────────────────────────────────────────────────

function CreateDispatchDrawer({ planDate, editDispatch, onClose, onSaved }) {
  const isEdit = !!editDispatch;
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [batches, setBatches] = useState({});
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(isEdit ? {
    dispatch_date: editDispatch.dispatch_date,
    customer_id: editDispatch.customer_id,
    customer_name: editDispatch.customer_name,
    transporter: editDispatch.transporter,
    vehicle_no: editDispatch.vehicle_no || '',
    group: editDispatch.group || '',
  } : {
    dispatch_date: planDate,
    customer_id: '', customer_name: '',
    transporter: '', vehicle_no: '', group: '',
  });
  const [lineItems, setLineItems] = useState(isEdit
    ? (editDispatch.line_items || []).map(li => ({
        order_id: li.order_id, order_no: li.order_no,
        product_id: li.product_id || '', product_name: li.product_name,
        sku: li.sku || '', quantity: String(li.quantity),
        unit: li.unit || 'Kgs', batch_no: li.batch_no || '', max_qty: 0,
      }))
    : []);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Load dispatchable orders, merging in any existing line item orders that may have fallen off the list
  async function loadOrders(cid, existingItems = []) {
    try {
      const { data } = await api.get('/orders/dispatchable', { params: { customer_id: cid } });
      const fetchedIds = new Set(data.map(o => o.id));
      const extraOrders = existingItems
        .filter(li => li.order_id && !fetchedIds.has(li.order_id))
        .map(li => ({
          id: li.order_id, order_no: li.order_no,
          product_name: li.product_name, product_sku: li.sku,
          remaining_qty: parseFloat(li.quantity) || 0,
        }));
      setOrders([...data, ...extraOrders]);
    } catch { setOrders([]); }
  }

  useEffect(() => {
    api.get('/customers').then(({ data }) => setCustomers(data)).catch(() => {});
    if (isEdit && editDispatch.customer_id) {
      loadOrders(editDispatch.customer_id, editDispatch.line_items || []);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCustomerChange(cid) {
    const c = customers.find(x => x.id === cid);
    setForm(p => ({ ...p, customer_id: cid, customer_name: c?.name || '' }));
    if (cid) {
      await loadOrders(cid);
    } else {
      setOrders([]);
    }
    setLineItems([]);
  }

  async function fetchBatches(sku) {
    if (!sku || batches[sku]) return;
    try {
      const { data } = await api.get('/inventory/stock', { params: { item_code: sku } });
      const batchItems = data.filter(i => i.batch_no);
      setBatches(prev => ({ ...prev, [sku]: batchItems }));
    } catch { setBatches(prev => ({ ...prev, [sku]: [] })); }
  }

  function addLineItem() {
    setLineItems(prev => [...prev, {
      order_id: '', order_no: '', product_id: '', product_name: '', sku: '',
      quantity: '', unit: 'Kgs', batch_no: '', max_qty: 0,
    }]);
  }

  function removeLineItem(idx) {
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  }

  function setLine(idx, k, v) {
    setLineItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [k]: v };
      return next;
    });
  }

  async function handleOrderSelect(idx, orderId) {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    setLine(idx, 'order_id', order.id);
    setLine(idx, 'order_no', order.order_no);
    setLine(idx, 'product_id', order.product_id || '');
    setLine(idx, 'product_name', order.product_name || '');
    setLine(idx, 'sku', order.product_sku || '');
    setLine(idx, 'max_qty', order.remaining_qty || 0);
    setLine(idx, 'quantity', String(order.remaining_qty || ''));
    setLine(idx, 'batch_no', '');
    if (order.product_sku) await fetchBatches(order.product_sku);
  }

  function handleBatchPill(idx, batch) {
    const item = lineItems[idx];
    const fillQty = Math.min(batch.qty_available, item.max_qty || batch.qty_available);
    setLine(idx, 'batch_no', batch.batch_no);
    setLine(idx, 'quantity', String(fillQty));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.customer_id) { alert('Select a customer'); return; }
    if (!form.transporter) { alert('Enter transporter name'); return; }
    if (lineItems.length === 0) { alert('Add at least one line item'); return; }
    const filledLines = lineItems.filter(l => l.order_id && l.quantity);
    if (filledLines.length === 0) { alert('Complete at least one line item'); return; }
    setSaving(true);
    try {
      const payload = {
        dispatch_date: form.dispatch_date,
        customer_id: form.customer_id,
        customer_name: form.customer_name,
        transporter: form.transporter,
        vehicle_no: form.vehicle_no || null,
        group: form.group || null,
        line_items: filledLines.map(l => ({
          order_id: l.order_id,
          order_no: l.order_no,
          product_id: l.product_id,
          product_name: l.product_name,
          sku: l.sku,
          quantity: parseFloat(l.quantity) || 0,
          unit: l.unit,
          batch_no: l.batch_no || null,
        })),
      };
      if (isEdit) {
        await api.put(`/dispatches/${editDispatch.id}`, payload);
      } else {
        await api.post('/dispatches', payload);
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || (isEdit ? 'Failed to update dispatch' : 'Failed to create dispatch'));
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,18%)', color: 'hsl(0,0%,90%)',
    padding: '6px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
    width: '100%', borderRadius: 0, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,45%)',
    textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, display: 'block',
  };
  const fieldStyle = { marginBottom: 14 };

  return (
    <div data-testid="create-dispatch-drawer"
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(480px, 100vw)',
        background: 'hsl(0,0%,5%)', borderLeft: '1px solid hsl(0,0%,14%)',
        zIndex: 400, overflowY: 'auto', padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16,
          color: 'hsl(0,0%,98%)', textTransform: 'uppercase' }}>{isEdit ? 'Edit Dispatch' : 'New Dispatch'}</div>
        <button onClick={onClose} data-testid="drawer-close"
          style={{ background: 'none', border: 'none', color: 'hsl(0,0%,55%)', cursor: 'pointer', padding: 4 }}>
          <X size={18} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Dispatch date */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Dispatch Date</label>
          <input type="date" style={inputStyle} value={form.dispatch_date}
            onChange={e => set('dispatch_date', e.target.value)} required />
        </div>

        {/* Customer */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Customer</label>
          <select style={inputStyle} value={form.customer_id}
            onChange={e => handleCustomerChange(e.target.value)} required>
            <option value="">Select customer…</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* Transporter */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Transporter</label>
          <input type="text" style={inputStyle} value={form.transporter}
            onChange={e => set('transporter', e.target.value)}
            placeholder="Transporter name" required />
        </div>

        {/* Vehicle no */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Vehicle No.</label>
          <input type="text" style={inputStyle} value={form.vehicle_no}
            onChange={e => set('vehicle_no', e.target.value)}
            placeholder="Optional" />
        </div>

        {/* Group */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Group / Truck No.</label>
          <input type="text" style={inputStyle} value={form.group}
            onChange={e => set('group', e.target.value)}
            placeholder="Optional — e.g. Truck 1, Morning" />
        </div>

        {/* Line items */}
        <div style={{ marginTop: 20, marginBottom: 8, borderTop: '1px solid hsl(0,0%,12%)', paddingTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 12,
              color: 'hsl(0,0%,80%)', textTransform: 'uppercase' }}>Line Items</span>
            <button type="button" data-testid="add-line-item" onClick={addLineItem}
              disabled={!form.customer_id}
              style={{ ...btnStyle, background: 'hsl(75,100%,8%)', color: 'hsl(75,100%,50%)',
                border: '1px solid hsl(75,100%,25%)',
                opacity: form.customer_id ? 1 : 0.4, cursor: form.customer_id ? 'pointer' : 'not-allowed' }}>
              + Add Line
            </button>
          </div>

          {lineItems.map((item, idx) => (
            <div key={idx} data-testid="line-item-row"
              style={{ marginBottom: 12, padding: 12, background: 'hsl(0,0%,7%)',
                border: '1px solid hsl(0,0%,12%)' }}>
              {/* Order picker */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Order</label>
                <select style={inputStyle} value={item.order_id}
                  onChange={e => handleOrderSelect(idx, e.target.value)}>
                  <option value="">Select order…</option>
                  {orders.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.order_no} — {o.product_name || o.product_sku} ({o.remaining_qty} remaining)
                    </option>
                  ))}
                </select>
              </div>

              {/* Batch pills (only when batches available) */}
              {item.sku && batches[item.sku]?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <label style={labelStyle}>Batch</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {batches[item.sku].map(b => (
                      <button key={b.batch_no} type="button" data-testid="batch-pill"
                        onClick={() => handleBatchPill(idx, b)}
                        style={{
                          padding: '3px 8px', borderRadius: 0, cursor: 'pointer',
                          fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                          background: item.batch_no === b.batch_no ? 'hsl(43,96%,20%)' : 'hsl(0,0%,10%)',
                          color: item.batch_no === b.batch_no ? 'hsl(43,96%,56%)' : 'hsl(0,0%,55%)',
                          border: item.batch_no === b.batch_no ? '1px solid hsl(43,96%,35%)' : '1px solid hsl(0,0%,18%)',
                        }}>
                        {b.batch_no} · {b.qty_available} Kgs
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Quantity</label>
                  <input type="number" style={inputStyle} value={item.quantity}
                    min={0} max={item.max_qty || undefined} step="0.01"
                    onChange={e => {
                      const raw = parseFloat(e.target.value);
                      if (isNaN(raw)) {
                        setLine(idx, 'quantity', e.target.value);
                      } else {
                        const capped = item.max_qty > 0 ? Math.min(raw, item.max_qty) : raw;
                        setLine(idx, 'quantity', String(capped));
                      }
                    }} />
                </div>
                <div style={{ width: 70 }}>
                  <label style={labelStyle}>Unit</label>
                  <select style={inputStyle} value={item.unit}
                    onChange={e => setLine(idx, 'unit', e.target.value)}>
                    <option>Kgs</option><option>Pcs</option><option>Ltrs</option>
                  </select>
                </div>
                <button type="button" onClick={() => removeLineItem(idx)} data-testid="remove-line-item"
                  style={{ ...btnStyle, padding: '6px 8px', marginBottom: 0,
                    background: 'hsl(0,84%,8%)', color: 'hsl(0,84%,50%)', border: '1px solid hsl(0,84%,20%)' }}>
                  <X size={12} />
                </button>
              </div>

              {/* Max qty hint */}
              {item.max_qty > 0 && (
                <div style={{ marginTop: 4, fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 9, color: 'hsl(0,0%,35%)' }}>
                  max {item.max_qty} {item.unit}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Submit */}
        <button type="submit" data-testid="submit-dispatch" disabled={saving}
          style={{ ...btnStyle, width: '100%', justifyContent: 'center', padding: '10px',
            fontSize: 12, background: 'hsl(75,100%,50%)', color: 'hsl(0,0%,3%)',
            border: 'none', marginTop: 8, opacity: saving ? 0.6 : 1 }}>
          {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Dispatch')}
        </button>
      </form>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function DispatchPlan() {
  const { user } = useAuth();
  const { activeLocation } = useLocationContext();
  const { notifications } = useRealtime();
  const [date, setDate] = useState(toDateStr(new Date()));
  const [dispatches, setDispatches] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editDispatch, setEditDispatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const load = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const params = { date };
      if (activeLocation) params.location = activeLocation;
      const { data } = await api.get('/dispatches', { params });
      setDispatches(data);
    } catch (e) {
      console.error('Failed to load dispatches', e);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [date, activeLocation]);

  useEffect(() => { load(); }, [load]);

  // Refresh when a dispatch-updated realtime event arrives
  const notifCount = notifications.length;
  useEffect(() => {
    if (notifCount > 0 && !loadingRef.current) load();
  }, [notifCount, load]);

  // Group dispatches by transporter (client-side)
  const byTransporter = useMemo(() => {
    const map = new Map();
    for (const d of dispatches) {
      const t = d.transporter || 'Unknown';
      if (!map.has(t)) map.set(t, []);
      map.get(t).push(d);
    }
    return map;
  }, [dispatches]);

  async function handleLockAll(transporter) {
    const planned = (byTransporter.get(transporter) || []).filter(d => d.status === 'planned');
    let failCount = 0;
    for (const d of planned) {
      try {
        await api.patch(`/dispatches/${d.id}/status`, { status: 'locked' });
      } catch (e) {
        console.error('Lock failed for', d.id, e);
        failCount++;
      }
    }
    if (failCount > 0) alert(`${failCount} dispatch(es) failed to lock.`);
    load();
  }

  const MONO_SM = { fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
    color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.12em' };

  return (
    <div data-testid="dispatch-plan-page">
      <style>{`
        /* ── Date nav on very small screens ── */
        @media (max-width: 480px) {
          [data-testid="dispatch-plan-page"] [data-testid="date-nav"] {
            flex-wrap: wrap; gap: 8px;
          }
          [data-testid="dispatch-plan-page"] [data-testid="date-nav"] > span {
            order: -1; width: 100%; text-align: center;
          }
        }
        /* ── Action buttons: larger touch targets on mobile ── */
        @media (max-width: 768px) {
          [data-testid="dispatch-row"] button {
            min-height: 36px;
            padding: 6px 12px !important;
            font-size: 11px !important;
          }
          [data-testid="dispatch-row"] [data-testid^="btn-"] {
            flex: 1;
            justify-content: center;
          }
        }
        /* ── Line items: stack on mobile instead of horizontal flex ── */
        @media (max-width: 640px) {
          [data-testid="dispatch-line-items"] > div {
            flex-direction: column !important;
            gap: 2px !important;
            padding: 6px 0 !important;
            border-bottom: 1px solid hsl(0,0%,10%);
          }
          [data-testid="dispatch-line-items"] > div > span {
            min-width: 0 !important;
            text-align: left !important;
          }
        }
        /* ── Backdrop: only on mobile ── */
        @media (max-width: 640px) {
          .dispatch-drawer-backdrop { display: block !important; }
        }
        /* ── New Dispatch button: full-width below heading on mobile ── */
        @media (max-width: 480px) {
          [data-testid="dispatch-plan-page"] > div:first-of-type {
            flex-direction: column !important;
            gap: 12px;
          }
          [data-testid="new-dispatch-btn"] {
            width: 100%; justify-content: center; padding: 10px !important;
            font-size: 12px !important; min-height: 44px;
          }
        }
        /* ── Create Dispatch: full-screen on mobile ── */
        @media (max-width: 640px) {
          [data-testid="create-dispatch-drawer"] {
            width: 100vw !important;
            padding: 16px !important;
          }
          [data-testid="create-dispatch-drawer"] input,
          [data-testid="create-dispatch-drawer"] select {
            font-size: 16px !important; /* prevents iOS auto-zoom */
            padding: 10px 12px !important;
            min-height: 44px;
          }
          [data-testid="create-dispatch-drawer"] button[type="submit"] {
            min-height: 48px;
            font-size: 13px !important;
          }
          [data-testid="add-line-item"],
          [data-testid="remove-line-item"] {
            min-height: 40px;
            padding: 8px 14px !important;
          }
          [data-testid="batch-pill"] {
            min-height: 36px;
            padding: 6px 10px !important;
            font-size: 11px !important;
          }
        }
      `}</style>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>
            Dispatch Plan
          </h1>
          <div style={MONO_SM}>Plan · Lock · Dispatch</div>
        </div>
        <button data-testid="new-dispatch-btn" onClick={() => setShowDrawer(true)}
          style={{ ...btnStyle, background: 'hsl(75,100%,50%)', color: 'hsl(0,0%,3%)',
            border: 'none', padding: '6px 14px', fontSize: 11 }}>
          + New Dispatch
        </button>
      </div>

      {/* Date navigation */}
      <div data-testid="date-nav" style={{ display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 20, padding: '8px 12px', background: 'hsl(0,0%,6%)',
        border: '1px solid hsl(0,0%,12%)' }}>
        <button data-testid="btn-prev-date" onClick={() => setDate(d => addDays(d, -1))}
          style={{ ...btnStyle, background: 'hsl(0,0%,10%)', color: 'hsl(0,0%,60%)',
            border: '1px solid hsl(0,0%,18%)' }}>
          <CaretLeft size={12} />
        </button>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13,
          color: 'hsl(0,0%,90%)', fontWeight: 600, flex: 1, textAlign: 'center' }}>
          {fmtDate(date)}
        </span>
        <button data-testid="btn-next-date" onClick={() => setDate(d => addDays(d, 1))}
          style={{ ...btnStyle, background: 'hsl(0,0%,10%)', color: 'hsl(0,0%,60%)',
            border: '1px solid hsl(0,0%,18%)' }}>
          <CaretRight size={12} />
        </button>
      </div>

      {/* Content */}
      {loading && (
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
          color: 'hsl(0,0%,40%)', padding: '20px 0' }}>Loading…</div>
      )}
      {!loading && dispatches.length === 0 && (
        <div data-testid="empty-state" style={{ fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, color: 'hsl(0,0%,35%)', padding: '40px 0', textAlign: 'center' }}>
          No dispatches planned for this date.
        </div>
      )}
      {!loading && Array.from(byTransporter.entries()).map(([transporter, tDispatches]) => (
        <TransporterGroup
          key={transporter}
          transporter={transporter}
          dispatches={tDispatches}
          userRole={user?.role}
          onStatusChange={load}
          onLockAll={handleLockAll}
          onEdit={d => { setEditDispatch(d); setShowDrawer(true); }}
        />
      ))}

      {showDrawer && (
        <>
          {/* Backdrop — visible on mobile, hidden on desktop where drawer slides in beside content */}
          <div onClick={() => { setShowDrawer(false); setEditDispatch(null); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
              zIndex: 399, display: 'none' }}
            className="dispatch-drawer-backdrop" />
          <CreateDispatchDrawer
            planDate={date}
            editDispatch={editDispatch}
            onClose={() => { setShowDrawer(false); setEditDispatch(null); }}
            onSaved={() => { setShowDrawer(false); setEditDispatch(null); load(); }}
          />
        </>
      )}
    </div>
  );
}
